import React, { useState, useCallback } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';

const COLS = ['#E05252','#5B9ED0','#4AC87A','#C8A84B','#C06090'];
const SIZE = 8;
const MAX_MOVES = 22;

function makeGrid() {
  return Array.from({ length: SIZE * SIZE }, () => Math.floor(Math.random() * COLS.length));
}

function flood(grid, color) {
  const newGrid = [...grid];
  const startColor = newGrid[0];
  if (startColor === color) return newGrid;
  const visited = new Uint8Array(SIZE * SIZE);
  const stack = [0];
  while (stack.length) {
    const i = stack.pop();
    if (visited[i]) continue;
    visited[i] = 1;
    newGrid[i] = color;
    const x = i % SIZE, y = Math.floor(i / SIZE);
    const neighbors = [];
    if (x > 0) neighbors.push(i - 1);
    if (x < SIZE - 1) neighbors.push(i + 1);
    if (y > 0) neighbors.push(i - SIZE);
    if (y < SIZE - 1) neighbors.push(i + SIZE);
    for (const n of neighbors) {
      if (!visited[n] && newGrid[n] === startColor) stack.push(n);
    }
  }
  return newGrid;
}

function countFilled(grid) {
  const c = grid[0];
  return grid.filter(v => v === c).length;
}

function ColorFloodGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const [grid, setGrid] = useState(() => makeGrid());
  const [moves, setMoves] = useState(0);
  const [phase, setPhase] = useState('playing'); // playing | won | lost

  const handleColor = useCallback((colorIdx) => {
    if (phase !== 'playing') return;
    const ng = flood(grid, colorIdx);
    const filled = countFilled(ng);
    const newMoves = moves + 1;
    setGrid(ng); setMoves(newMoves);
    if (filled === SIZE * SIZE) {
      saveGameScore('colorflood', Math.round(((MAX_MOVES - newMoves) / MAX_MOVES) * 100 + 10));
      setPhase('won');
    } else if (newMoves >= MAX_MOVES) {
      setPhase('lost');
    }
  }, [grid, moves, phase]);

  const restart = () => { setGrid(makeGrid()); setMoves(0); setPhase('playing'); };

  return (
    <div>
      <button onClick={() => onBack()} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>■ {nl ? 'Stop' : 'Stop'}</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ color: C.silver, fontSize: 12 }}>{nl ? `Zetten: ${moves}/${MAX_MOVES}` : `Moves: ${moves}/${MAX_MOVES}`}</span>
        <span style={{ color: _T.accent, fontSize: 12 }}>{nl ? 'Vul het bord!' : 'Fill the board!'}</span>
      </div>

      {/* Move bar */}
      <div style={{ height: 4, borderRadius: 4, background: _T.bg4, marginBottom: 12 }}>
        <div style={{ height: '100%', width: `${(moves / MAX_MOVES) * 100}%`, background: moves > MAX_MOVES * 0.8 ? C.red : _T.accent, borderRadius: 4, transition: 'width 0.2s' }} />
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${SIZE}, 1fr)`, gap: 2, marginBottom: 14, borderRadius: 10, overflow: 'hidden' }}>
        {grid.map((c, i) => (
          <div key={i} style={{ paddingTop: '100%', background: COLS[c], borderRadius: 2, transition: 'background 0.15s' }} />
        ))}
      </div>

      {/* Color buttons */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
        {COLS.map((col, i) => (
          <button key={i} onClick={() => handleColor(i)}
            style={{ width: 44, height: 44, borderRadius: 12, border: `3px solid ${grid[0] === i ? '#fff' : 'transparent'}`, background: col, cursor: 'pointer', transition: 'transform 0.1s', transform: grid[0] === i ? 'scale(1.1)' : 'scale(1)' }} />
        ))}
      </div>

      {phase !== 'playing' && (
        <div style={{ ...s_GC({ padding: 18, textAlign: 'center', marginBottom: 12 }) }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>{phase === 'won' ? '🎉' : '😅'}</div>
          <div style={{ color: C.white, fontWeight: 700, marginBottom: 4 }}>{phase === 'won' ? (nl ? `Gewonnen in ${moves} zetten!` : `Won in ${moves} moves!`) : (nl ? 'Geen zetten meer!' : 'Out of moves!')}</div>
          <button style={s_PB({ width: '100%', marginTop: 8 })} onClick={restart}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default ColorFloodGame;
